<template>
  <div>
    <!-- Header -->
    <div class="relative flex justify-center items-center">
      <div class="absolute z-30 text-white px-6">
        <h1 class="text-4xl md:text-5xl leading-[50px] md:leading-[60px] font-serif text-center">{{ $t('services.index.title') }}</h1>
      </div>
      <div class="absolute z-20 inset-0 bg-black opacity-40"></div>
      <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" class="object-cover min-h-[400px] w-full md:aspect-[3]" src="/images/services/hero.jpg"
        alt="Services Hero Image" />
    </div>

    <div class="bg-gray-100">
      <div class="container mx-auto">
        <p class="text-sm md:text-base px-6 py-12 md:p-20" data-aos="fade-right">{{ $t('services.index.description') }}</p>
      </div>

    </div>

    <div class="bg-gray-100 flex flex-col gap-12 md:gap-0 pb-12 md:pb-0">
      <!-- Listening Desk -->
      <div class="bg-white overflow-hidden rounded-lg md:rounded-none md mx-6 md:mx-0 flex flex-col md:flex-row shadow-lg transition-shadow">
        <div class="w-full md:w-1/2 relative aspect-[3/2] overflow-hidden">
          <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" src="/images/services/listening-desk-hero.jpg" alt="Listening Desk Service"
            class="object-cover w-full h-full" />
        </div>
        <div
          class="md:w-1/2 flex flex-col justify-center p-10 md:p-16 gap-4 text-center md:text-left items-center md:items-start">
          <div class="flex items-center gap-4 justify-center md:justify-start">
            <div class="bg-blue-600 rounded-full h-10 w-10 flex items-center justify-center">
              <Icon class="text-white" name="mdi:ear-hearing" size="24"></Icon>
            </div>
            <p class="text-base md:text-lg lg:text-xl font-serif">{{ $t('services.index.listening-desk') }}</p>
          </div>
          <p class="text-sm md:text-base text-gray-600 mb-8">{{ $t('services.index.listening-desk-description') }}</p>
          <NuxtLink :to="localePath('/services/listening-desk')" class="w-full xl:w-auto">
            <Button class="bg-blue-600 border-2 w-full xl:w-auto">
              <div>{{ $t('services.index.learn-more') }}</div>
            </Button>
          </NuxtLink>
        </div>
      </div>

      <!-- Educational Support -->
      <div class="bg-white overflow-hidden rounded-lg md:rounded-none md mx-6 md:mx-0 flex flex-col md:flex-row-reverse shadow-lg transition-shadow">
        <div class="w-full md:w-1/2 relative aspect-[3/2] overflow-hidden">
          <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" src="/images/services/educational-support-hero.jpg" alt="Educational Support Service"
            class="object-cover w-full h-full" />
        </div>
        <div
          class="md:w-1/2 flex flex-col justify-center p-10 md:p-16 gap-4 text-center md:text-left items-center md:items-start">
          <div class="flex items-center gap-4 justify-center md:justify-start">
            <div class="bg-green-600 rounded-full h-10 w-10 flex items-center justify-center">
              <Icon class="text-white" name="mdi:book-open-variant" size="24"></Icon>
            </div>
            <p class="text-base md:text-lg lg:text-xl font-serif">{{ $t('services.index.educational-support') }}</p>
          </div>
          <p class="text-sm md:text-base text-gray-600 mb-8">{{ $t('services.index.educational-support-description') }}</p>
          <NuxtLink :to="localePath('/services/educational-support')" class="w-full xl:w-auto">
            <Button class="bg-green-600 border-2 w-full xl:w-auto">
              <div>{{ $t('services.index.learn-more') }}</div>
            </Button>
          </NuxtLink>
        </div>
      </div>

      <!-- Training Paths -->
      <div class="bg-white overflow-hidden rounded-lg md:rounded-none md mx-6 md:mx-0 flex flex-col md:flex-row shadow-lg transition-shadow">
        <div class="w-full md:w-1/2 relative aspect-[3/2] overflow-hidden">
          <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" src="/images/services/training-paths-hero.jpg" alt="Training Paths Service"
            class="object-cover w-full h-full" />
        </div>
        <div
          class="md:w-1/2 flex flex-col justify-center p-10 md:p-16 gap-4 text-center md:text-left items-center md:items-start">
          <div class="flex items-center gap-4 justify-center md:justify-start">
            <div class="bg-orange-600 rounded-full h-10 w-10 flex items-center justify-center">
              <Icon class="text-white" name="mdi:school-outline" size="24"></Icon>
            </div>
            <p class="text-base md:text-lg lg:text-xl font-serif">{{ $t('services.index.training-paths') }}</p>
          </div>
          <p class="text-sm md:text-base text-gray-600 mb-8">{{ $t('services.index.training-paths-description') }}</p>
          <NuxtLink :to="localePath('/services/training-paths')" class="w-full xl:w-auto">
            <Button class="bg-orange-600 border-2 w-full xl:w-auto">
              <div>{{ $t('services.index.learn-more') }}</div>
            </Button>
          </NuxtLink>
        </div>
      </div>

      <!-- Psychological Services -->
      <div class="bg-white overflow-hidden rounded-lg md:rounded-none md mx-6 md:mx-0 flex flex-col md:flex-row-reverse shadow-lg transition-shadow">
        <div class="w-full md:w-1/2 relative aspect-[3/2] overflow-hidden">
          <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" src="/images/services/psychological-services-hero.jpg" alt="Psychological Services"
            class="object-cover w-full h-full" />
        </div>
        <div
          class="md:w-1/2 flex flex-col justify-center p-10 md:p-16 gap-4 text-center md:text-left items-center md:items-start">
          <div class="flex items-center gap-4 justify-center md:justify-start">
            <div class="bg-red-600 rounded-full h-10 w-10 flex items-center justify-center">
              <Icon class="text-white" name="mdi:brain" size="24"></Icon>
            </div>
            <p class="text-base md:text-lg lg:text-xl font-serif">{{ $t('services.index.psychological-services') }}</p>
          </div>
          <p class="text-sm md:text-base text-gray-600 mb-8">{{ $t('services.index.psychological-services-description') }}</p>
          <NuxtLink :to="localePath('/services/psychological-services')" class="w-full xl:w-auto">
            <Button class="bg-red-600 border-2 w-full xl:w-auto">
              <div>{{ $t('services.index.learn-more') }}</div>
            </Button>
          </NuxtLink>
        </div>
      </div>

      <!-- Workshops -->
      <div class="bg-white overflow-hidden rounded-lg md:rounded-none md mx-6 md:mx-0 flex flex-col md:flex-row shadow-lg md:shadow-none transition-shadow">
        <div class="w-full md:w-1/2 relative aspect-[3/2] overflow-hidden">
          <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw" src="/images/services/workshops-hero.jpg" alt="Workshop Activities" class="object-cover w-full h-full" />
        </div>
        <div
          class="md:w-1/2 flex flex-col justify-center p-10 md:p-16 gap-4 text-center md:text-left items-center md:items-start">
          <div class="flex items-center gap-4 justify-center md:justify-start">
            <div class="bg-purple-600 rounded-full h-10 w-10 flex items-center justify-center">
              <Icon class="text-white" name="mdi:palette" size="24"></Icon>
            </div>
            <p class="text-base md:text-lg lg:text-xl font-serif">{{ $t('services.index.workshops') }}</p>
          </div>
          <p class="text-sm md:text-base text-gray-600 mb-8">{{ $t('services.index.workshops-description') }}</p>
          <NuxtLink :to="localePath('/services/workshops')" class="w-full xl:w-auto">
            <Button class="bg-purple-600 border-2 w-full xl:w-auto">
              <div>{{ $t('services.index.learn-more') }}</div>
            </Button>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
</script>
